import { useCallback, useEffect, useState } from "react";
import type {
  ConfirmationQueueEntryV1,
  ImportDraftReviewViewV1,
  SubmitImportSuggestionDecisionV1,
} from "@enrein/shared-contracts";
import type { StudioApiClient } from "../../../api.js";
import { GuidedButton, GuidedFlowCopy } from "../primitives/GuidedPrimitives.js";

export function GuidedConfirmationQueueView({
  api,
  onQueueChanged,
}: {
  readonly api: StudioApiClient;
  readonly onQueueChanged?: () => void;
}) {
  const [entries, setEntries] = useState<ConfirmationQueueEntryV1[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [review, setReview] = useState<ImportDraftReviewViewV1 | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<Record<string, SubmitImportSuggestionDecisionV1>>({});

  const refreshQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queue = await api.listImportConfirmationQueue();
      setEntries(queue.entries);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadReview = useCallback(
    async (importDraftId: string) => {
      setBusy(true);
      setError(null);
      try {
        const view = await api.getImportDraftReview(importDraftId);
        setReview(view);
        setDecisions({});
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
        setReview(null);
      } finally {
        setBusy(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void refreshQueue();
  }, [refreshQueue]);

  useEffect(() => {
    if (!selectedId) {
      setReview(null);
      return;
    }
    void loadReview(selectedId);
  }, [loadReview, selectedId]);

  async function submitDecisions() {
    if (!selectedId || !review) return;
    const payload = Object.values(decisions);
    if (payload.length === 0) {
      setError("Select at least one decision.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.submitImportDraftDecisions(selectedId, {
        schemaId: "importConfirmation.submitDecisionsRequest.v1",
        decisions: payload,
      });
      setMessage("Decisions saved.");
      await loadReview(selectedId);
      await refreshQueue();
      onQueueChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  async function applyToDraft() {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.applyImportConfirmationToDraft(selectedId);
      setMessage(`Applied ${result.appliedFieldCount} field(s) to draft ${result.appliedDraftId}.`);
      await refreshQueue();
      await loadReview(selectedId);
      onQueueChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="enrein-guided-confirmation-queue">
      {loading ? <p className="enrein-guided-muted">Loading queue…</p> : null}
      {!loading && entries.length === 0 ? (
        <p className="enrein-guided-muted">No import drafts awaiting confirmation.</p>
      ) : (
        <ul className="enrein-guided-opportunity-list">
          {entries.map((entry) => (
            <li key={entry.importDraftId}>
              <button
                type="button"
                className={
                  selectedId === entry.importDraftId
                    ? "enrein-guided-opportunity-item is-active"
                    : "enrein-guided-opportunity-item"
                }
                onClick={() => setSelectedId(entry.importDraftId)}
              >
                <span>{entry.sourceUrl}</span>
                <span className="enrein-guided-chip">
                  {entry.pendingDecisionCount > 0 ? `${entry.pendingDecisionCount} pending` : entry.reviewStatus}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {review ? (
        <div className="enrein-guided-embedded-panel">
          <GuidedFlowCopy title="Review import draft" kicker={review.reviewStatus}>
            <p className="enrein-guided-muted">{review.sourceUrl}</p>
          </GuidedFlowCopy>
          <ul className="enrein-guided-suggestion-list">
            {review.items.map(({ suggestion, decision }) => (
              <li key={suggestion.suggestionId} className="enrein-guided-suggestion-row">
                <div>
                  <strong>{suggestion.fieldKind}</strong>
                  <p>{suggestion.value}</p>
                  {decision ? <p className="enrein-guided-muted">Saved: {decision.decision}</p> : null}
                </div>
                {!decision ? (
                  <div className="enrein-guided-btn-row">
                    {(["CONFIRMED", "REJECTED", "DEFERRED"] as const).map((kind) => (
                      <GuidedButton
                        key={kind}
                        variant="line"
                        disabled={busy}
                        onClick={() =>
                          setDecisions((current) => ({
                            ...current,
                            [suggestion.suggestionId]: {
                              schemaId: "importConfirmation.submitDecision.v1",
                              suggestionId: suggestion.suggestionId,
                              decision: kind,
                            },
                          }))
                        }
                      >
                        {kind.toLowerCase()}
                      </GuidedButton>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="enrein-guided-btn-row">
            <GuidedButton variant="gold" disabled={busy} onClick={() => void submitDecisions()}>
              Save decisions
            </GuidedButton>
            <GuidedButton variant="line" disabled={busy} onClick={() => void applyToDraft()}>
              Apply to Business Profile draft
            </GuidedButton>
          </div>
        </div>
      ) : null}

      {message ? <p className="enrein-guided-muted">{message}</p> : null}
      {error ? <p className="enrein-guided-validation">{error}</p> : null}
    </div>
  );
}
